import { returnAllTradeDetailsFromGenericParams } from "@/lib/agGridColDefs/colDefGlobalHelpers";
import { ExecutionRequestStatus } from "@/lib/interfaces and Enums/ENUMExecutionRequestStatus";
import { ExecutionStrategyStatus } from "@/lib/interfaces and Enums/ENUMExecutionStrategyStatus";
import { StrategyType } from "@/lib/interfaces and Enums/ENUMStrategyType";
import { IExecutionRequestCounterParties } from "@/lib/interfaces and Enums/InterfaceExecutionRequestCounterParties";
import { ExecutionRequestQuoteLeg } from "@/lib/interfaces and Enums/InterfaceExecutionRequestQuoteLeg";
import {
  ExecutionStrategy,
  ISwapTradeFees,
} from "@/lib/interfaces and Enums/InterfaceExecutionStrategy";
import useTableUIStore from "@/store/tableUIStore";
import tradeDataStore from "@/store/tradeDataStore";
import { MenuItem, Select } from "@mui/material";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import { getGridSelectColors } from "@/lib/styles/gridSelectStyles";
import {
  statusAtAffirmedRange,
  statusAtExecutedRange,
  statusFormExecutedToAffirmedRange,
  statusFromAwarded,
  statusFromReceivedToQuoted,
} from "../colDefs/colDefsShared";
import {
  getCellElementHeight,
  getCellFontSizeRem,
} from "../actionSummaryHelper";
import { returnBlockId } from "../actionSummaryColDefHelperFunctions";
import { isAbleToShowCashColumn } from "./CashCellRenderer";
import { findCPOnTrade } from "../tradeLookupHelpers";

export function showNeedToRecouponOrEnterCash(
  params: any,
  thisTrade: ExecutionStrategy,
  isOutright: boolean,
): boolean {
  //Seperate need for either OR or RP messages.
  if (thisTrade.role == "RP") {
    if (thisTrade.unwind == true) {
      const blockID = returnBlockId(thisTrade, params);
      if (!blockID) {
        // Will be RP - ER level now.
        if (thisTrade && thisTrade.fees && thisTrade.fees.length > 0) {
          const currentFeeObject = thisTrade.fees[0];
          if (currentFeeObject) {
            const currentTransactionFee = currentFeeObject.transactionFee;

            if (currentTransactionFee == undefined) {
              return true;
            }
          }
        }
      } else {
        // RP LEG NPV STYLE
        if (thisTrade && thisTrade.legs) {
          const thisBlock = thisTrade.legs.find(
            (leg) => leg.blockId == blockID,
          );
          const currentFeeObject = thisBlock?.fees?.[0];
          if (currentFeeObject) {
            const currentTransactionFee = currentFeeObject.transactionFee;

            if (currentTransactionFee == undefined) {
              return true;
            }
          }
        }
      }
    } else if (
      thisTrade?.status &&
      thisTrade.unwind == false && // 14599 NON NPV STYLE only.
      [
        ExecutionStrategyStatus.Received,
        ExecutionRequestStatus.Received,
        ExecutionStrategyStatus.ReSent,
        ExecutionRequestStatus.ReSent,
        ExecutionStrategyStatus.Awarded,
        ExecutionRequestStatus.Awarded,
      ].includes(thisTrade.status)
      // #14876, #14877, #14878, #14879, #14880, #14881 - Recoupon at awarded also requires warning message at Awarded.
    ) {
      if (isOutright) {
        if (thisTrade.legs && thisTrade.legs.length > 0) {
          const currentLeg = thisTrade.legs[0];
          if (currentLeg && currentLeg.fees && currentLeg.fees.length > 0) {
            const currentFeeObject = currentLeg.fees.find(
              (feeObj: ISwapTradeFees) =>
                feeObj.transactionFeeSide == currentLeg.quoteSide,
            );

            if (currentFeeObject) {
              const currentTransactionFee = currentFeeObject.transactionFee;
              if (currentTransactionFee == undefined) {
                return true;
              }
            }
          }
        }
      } else {
        // Logic block used for NPV blocks yet to be quoted.
        const blockID = params.data.blockId;
        if (blockID) {
          // Block id = leg logic
          const currentLeg = thisTrade.legs?.find(
            (leg) => leg.blockId == blockID,
          );

          if (currentLeg && currentLeg.fees && currentLeg.fees.length > 0) {
            const currentFeeObject = currentLeg.fees.find(
              (feeObj: ISwapTradeFees) =>
                feeObj.transactionFeeSide == currentLeg.quoteSide,
            );

            if (currentFeeObject) {
              const currentTransactionFee = currentFeeObject.transactionFee;
              if (currentTransactionFee == undefined) {
                return true;
              }
            }
          }
        }
      }
    }
  } else if (thisTrade.role == "OP") {
    if (params.data.blockId) {
      // NOT an outright - Normal Leg
      if (
        thisTrade.status &&
        thisTrade.status == ExecutionStrategyStatus.Awarded //14936 - Show info for OP now at awarded.
      ) {
        const thisBlockId = params.data.blockId;

        if (
          !thisTrade.executionRequestCounterParties ||
          thisTrade.executionRequestCounterParties.length == 0
        ) {
          return false;
        }

        const matchingErcp = thisTrade.executionRequestCounterParties.filter(
          (er) => er.legs?.find((block) => block.blockId == thisBlockId),
        )[0];

        const thisBlock = matchingErcp?.legs?.find(
          (leg) => leg.blockId == thisBlockId,
        );

        if (!thisBlock || !thisBlock.fees) {
          return false;
        }

        const thisFeeObject = thisBlock.fees.find(
          (object) => object.transactionFeeSide == thisBlock.quoteSide,
        );

        if (!thisFeeObject) {
          return false;
        }

        if (thisFeeObject.transactionFee != null) {
          return false;
        } else {
          //14600 - red info button on OP's recoupon blocks at status "Executed" if there are no transaction fees
          return true;
        }
      }
    } else if (
      params.data.executionRequestId &&
      params.data.legs &&
      params.data.legs.length == 1 &&
      thisTrade.strategyType == StrategyType.Outright
    ) {
      // Outright at exectued follow the same as the leg rules above but don't need to 'find' the leg.
      if (
        !thisTrade ||
        !thisTrade.executionRequestCounterParties ||
        thisTrade.executionRequestCounterParties.length == 0
      ) {
        return false;
      }

      const thisLeg = findCPOnTrade(thisTrade, params.data.executionRequestId)?.legs?.[0];

      if (!thisLeg) {
        return false;
      }

      // early return on outrights, only show the warning if the ER status is actually exectued
      const currentStatus = thisLeg.statusER as ExecutionRequestStatus;
      if (currentStatus) {
        if (!statusFromAwarded.includes(currentStatus)) {
          return false;
        }
      }

      const thisFeeObject = thisLeg.fees?.find(
        (object: any) => object.transactionFeeSide == thisLeg.quoteSide,
      );
      if (!thisFeeObject) {
        return false;
      }

      if (thisFeeObject.transactionFee != null) {
        return false;
      } else {
        return true;
      }
    }
  }
  return false;
}

export function returnFeeDirectionObjectKeyRPLeg({
  status,
  unwind,
  userUpdatedDirection,
  legUnwind,
}: {
  status: ExecutionRequestStatus;
  unwind: boolean | undefined;
  userUpdatedDirection?: "NPV" | "Recoupon";
  legUnwind?: "NPV" | "Recoupon" | null | undefined;
}): keyof ISwapTradeFees {
  if (userUpdatedDirection) {
    return "userUpdatedDirection";
  }
  if (status && unwind) {
    if (
      !statusAtAffirmedRange.includes(status) &&
      !statusAtExecutedRange.includes(status)
    ) {
      // 14310: Awarded and below: Dealer read from ---- indicativeFeeDirection
      return "indicativeFeeDirection";
    } else {
      if (statusAtExecutedRange.includes(status)) {
        // 14310: Exectued Dealer read from  ---- transactionFeeDirection
        return "transactionFeeDirection";
      } else if (
        statusAtAffirmedRange.includes(status) ||
        status == ExecutionRequestStatus.DealtAway // RP can be Dealt away. 15042
      ) {
        // 14310: Affirmed Dealer read from ---- dealtFeeDirection
        return "dealtFeeDirection";
      }
    }
  } else if (status) {
    // Non-NPV Style Trade:
    if (
      statusAtAffirmedRange.includes(status) ||
      status == ExecutionRequestStatus.DealtAway // RP can be Dealt away. 15042
    ) {
      // 14300: Affirmed Dealer read from ---- dealtFeeDirection Both NPV and Recoupon
      return "dealtFeeDirection";
    }
    if (legUnwind == "NPV") {
      return "transactionFeeDirection";
    } else if (legUnwind == "Recoupon") {
    }
  }
  return "indicativeFeeDirection";
}

export function returnFeeObjectKeyRPatER(
  status: ExecutionRequestStatus,
): keyof ISwapTradeFees {
  if (statusFromReceivedToQuoted.includes(status as ExecutionRequestStatus)) {
    return "indicativeFeeDirection";
  }
  return "transactionFeeDirection";
}

export function returnDealerValueRPatER({
  thisTrade,
}: {
  thisTrade: ExecutionStrategy;
}): "Pays" | "Receives" | "NotFound" {
  //Early return if dealer has already been applied:
  if (!thisTrade.fees || thisTrade.fees.length == 0) {
    return "NotFound";
  }

  const thisFeeObject = thisTrade.fees.find(
    (object) => object.transactionFeeSide == thisTrade.quoteSideER,
  );

  if (!thisFeeObject) {
    return "NotFound";
  }

  if (thisFeeObject.userUpdatedDirection) {
    // Early Return -- The user has made a selection with is not captured and used
    return thisFeeObject.userUpdatedDirection;
  }

  if (thisTrade.status) {
    if (statusAtAffirmedRange.includes(thisTrade.status)) {
      const dealer = thisFeeObject.dealtFeeDirection;
      return dealer;
    }

    if (
      statusFromReceivedToQuoted.includes(thisTrade.status) ||
      thisTrade.status == ExecutionRequestStatus.DealtAway // Updated via: 15099 OLD: RP can be Dealt away. 15042
    ) {
      const currentFee = thisFeeObject.transactionFee;
      // 0 is a valid entered fee; only fall back to indicative when nothing was entered.
      if (currentFee != null) {
        const dealer = thisFeeObject.transactionFeeDirection;
        return dealer;
      } else {
        const dealer = thisFeeObject.indicativeFeeDirection;
        return dealer;
      }
    }
  }

  const dealer = thisFeeObject.transactionFeeDirection;
  return dealer;
}

export function returnDealerValueRPatLeg({
  data,
  thisTrade,
  isOutright,
}: {
  data: ExecutionRequestQuoteLeg; //Params will always be stale.
  thisTrade: ExecutionStrategy;
  isOutright: boolean;
}): "Pays" | "Receives" | "NotFound" {
  if (thisTrade) {
    const thisBlockId = !isOutright
      ? data.blockId
      : thisTrade.legs?.[0]?.blockId;
    const thisBlock = thisTrade.legs?.find((leg) => leg.blockId == thisBlockId);
    if (!thisBlock) {
      return "NotFound";
    }

    if (!thisBlock.fees || thisBlock.fees.length < 0) {
      return "NotFound";
    }

    const thisFeeObject = thisBlock.fees.find(
      (object) => object.transactionFeeSide == thisBlock.quoteSide,
    );

    if (!thisFeeObject) {
      return "NotFound";
    }

    if (thisFeeObject.userUpdatedDirection) {
      // Early Return -- The user has made a selection with is not captured and used
      return thisFeeObject.userUpdatedDirection;
    }

    // NONE of this will run if the user has selected.
    if (thisTrade.status) {
      const status = thisTrade.status as ExecutionRequestStatus;
      const unwind = thisTrade.unwind;

      if (unwind) {
        // unwind == true means NPV style (14310)
        const feeKey = returnFeeDirectionObjectKeyRPLeg({ status, unwind });
        const dealer = thisFeeObject[feeKey];
        if ((dealer && dealer == "Pays") || (dealer && dealer == "Receives")) {
          return dealer;
        }
      } else {
        // 14300 Non NPV style:
        // Both NPV and Recoupon when at status Affirmed.
        if (statusAtAffirmedRange.includes(status)) {
          const dealer = thisFeeObject.dealtFeeDirection;
          return dealer;
        }
        if (
          thisBlock.unwind == "Recoupon" ||
          status == ExecutionRequestStatus.DealtAway // Updated via: 15099 OLD:  RP can be Dealt away. 15042
        ) {
          // 14300 - Recoupon
          const currentFee = thisFeeObject.transactionFee;
          // 0 is a valid entered fee; only fall back to indicative when nothing was entered.
          if (currentFee != null) {
            const dealer = thisFeeObject.transactionFeeDirection;
            return dealer;
          } else {
            const dealer = thisFeeObject.indicativeFeeDirection;
            return dealer;
          }
        } else if (thisBlock.unwind == "NPV") {
          // 14300 - NPV
          const dealer = thisFeeObject.transactionFeeDirection;
          return dealer;
        }
      }
    }
    return thisFeeObject.transactionFeeDirection;
  }
  return "NotFound";
}

export function returnDealerValueOPatER({
  data,
  thisTrade,
}: {
  data: IExecutionRequestCounterParties; //Data will always be stale.
  thisTrade: ExecutionStrategy;
}): "Pays" | "Receives" | "NotFound" {
  const thisERId = data.executionRequestId;
  const thisExecutionRequest = findCPOnTrade(thisTrade, thisERId);
  if (!thisExecutionRequest) {
    return "NotFound";
  }
  if (
    !thisExecutionRequest ||
    !thisExecutionRequest.fees ||
    thisExecutionRequest.fees.length < 0
  ) {
    return "NotFound";
  }

  const thisFeeObject = thisExecutionRequest.fees.find(
    (object: any) => object.transactionFeeSide == thisExecutionRequest.quoteSideER,
  );

  if (!thisFeeObject) {
    return "NotFound";
  }

  if(thisTrade.status) {
    if(statusAtAffirmedRange.includes(thisTrade.status)) {
      const dealer = thisFeeObject.dealtFeeDirection
      return dealer
    }

    if(
      statusFromReceivedToQuoted.includes(thisTrade.status) ||
      thisTrade.status == ExecutionRequestStatus.DealtAway
    ) {
      const currentFee = thisFeeObject.transactionFee;

      if(currentFee != null) {
        const dealer = thisFeeObject.transactionFeeDirection;
        return dealer;
      } else {
        const dealer = thisFeeObject.indicativeFeeDirection;
        return dealer;
      }
    }
  }

  const dealer = thisFeeObject.transactionFeeDirection;
  return dealer;
}

export function returnDealerValueOPatLeg({
  data,
  thisTrade,
  isOutright,
}: {
  data: IExecutionRequestCounterParties; //data will always be stale.
  thisTrade: ExecutionStrategy;
  isOutright: boolean;
}): "Pays" | "Receives" | "NotFound" {
  if (thisTrade) {
    const thisBlockId = !isOutright ? data.blockId : data.legs?.[0]?.blockId;
    let thisLeg: ExecutionRequestQuoteLeg | null = null;

    thisTrade.executionRequestCounterParties?.forEach((ERCPs) => {
      const found = ERCPs.legs?.find((leg) => leg.blockId == thisBlockId);
      if (found) {
        thisLeg = found;
      }
    });

    if (thisLeg && thisLeg != null) {
      const thisLegFound = thisLeg as ExecutionRequestQuoteLeg;

      if (!thisLegFound.fees || thisLegFound.fees.length < 0) {
        return "NotFound";
      }

      const thisFeeObject = thisLegFound.fees.find(
        (object) => object.transactionFeeSide == thisLegFound.quoteSide,
      );

      if (!thisFeeObject) {
        return "NotFound";
      }


      if (thisLegFound.statusER) {
        const status = thisLegFound.statusER as ExecutionRequestStatus;
        const unwind = thisTrade.unwind;
        
        if(unwind) {
          //NPV style trade
          const feeKey = returnFeeDirectionObjectKeyRPLeg({status, unwind});
          const dealer = thisFeeObject[feeKey];

          if ((dealer && dealer == "Pays") || (dealer && dealer == "Receives")) {
            return dealer;
          } 
        } else {
          if(statusAtAffirmedRange.includes(status)){
            const dealer = thisFeeObject.dealtFeeDirection;
            return dealer;
          }

          if(
            thisLegFound.unwind == "Recoupon" || 
            status == ExecutionRequestStatus.DealtAway
          ) {
            //Recoupn style trade
            const currentFee = thisFeeObject.transactionFee;

            if(currentFee != null) {
              const dealer = thisFeeObject.transactionFeeDirection;
              return dealer;
            } else {
              const dealer = thisFeeObject.indicativeFeeDirection;
              return dealer
            }
          } else if (thisLegFound.unwind == "NPV") {
            const dealer = thisFeeObject.transactionFeeDirection;
            return dealer
          }
        }
      }
      const dealer = thisFeeObject.transactionFeeDirection;
      return dealer;
    }
  }
  return "NotFound";
}

export function determineDealerFromImpliedValues(
  params: any,
  thisTrade: ExecutionStrategy,
  isOutright: boolean,
): "Pays" | "Receives" | null {
  // Dealer is a temp field stored in Zustand that NEVER travels across the Network
  // The front end will create a field for Dealer - if it exists, it should be used.
  // In the case the 'Dealer' filed is not in the Zustand store, the front end will recalculate it and store it again.

  // Early return if the field is found:
  // EACH return type will need to look for it's filed to early return of the nested data version of 'this trade':
  const { isOp, isLeg, isER } = returnAllTradeDetailsFromGenericParams(params);
  // ---- RP
  if (!isOp && isER && !isOutright) {
    const dealer = returnDealerValueRPatER({ thisTrade });
    if (dealer != "NotFound") {
      return `${dealer}`;
    }
  } else if ((!isOp && isLeg) || (!isOp && isOutright)) {
    const dealer = returnDealerValueRPatLeg({
      data: params.data,
      thisTrade,
      isOutright,
    });
    if (dealer != "NotFound") {
      return `${dealer}`;
    }
    // ---- OP
  } else if (isOp && isER && !isOutright) {
    const dealer = returnDealerValueOPatER({ data: params.data, thisTrade });
    if (dealer != "NotFound") {
      return `${dealer}`;
    } else {
      return null; // Per 14390 potential for an empty value in the cell.
    }
  } else if ((isOp && isLeg) || (isOp && isOutright)) {
    const dealer = returnDealerValueOPatLeg({
      data: params.data,
      thisTrade,
      isOutright,
    });
    if (dealer != "NotFound") {
      return `${dealer}`;
    }
  }
  // Final Catch all - One of the above has returned "NotFound"
  return null;
}

// Whether the cell has a dealer direction to show at all - NPV style trade, or an NPV/Recoupon leg.
export function shouldRenderDealerCell(
  params: any,
  unwind: boolean | undefined,
  isLeg: boolean,
  isOutright: boolean,
): boolean {
  if (unwind == true) {
    return true;
  }
  if (unwind == false && isLeg) {
    return params.data?.unwind == "Recoupon" || params.data?.unwind == "NPV";
  }
  if (unwind == false && isOutright) {
    const legUnwind = params.data?.legs?.[0]?.unwind;
    return legUnwind == "NPV" || legUnwind == "Recoupon";
  }
  return false;
}

const DealerCellRenderer = (params: any) => {
  const { isOp, fullTradeData, isLeg, isER, isOutright } =
    returnAllTradeDetailsFromGenericParams(params);

  const currentTradeData = tradeDataStore((state) => state.tradeData);
  const thisTrade =
    currentTradeData.find((trade) => trade.esid == fullTradeData.esid) ??
    fullTradeData;

  // Per 14390 potential for an empty value in the cell.
  // If determineDealerFromImpliedValues does not return null intentionally, "Pay wil"
  const [selectedDealer, setSelectedDealer] = useState<
    "Pays" | "Receives" | null
  >(
    thisTrade
      ? determineDealerFromImpliedValues(params, thisTrade, isOutright)
      : "Pays",
  );

  const unwind = fullTradeData.unwind;

  // //   const status: ExecutionStrategyStatus =
  // //     fullTradeData.status as ExecutionStrategyStatus;

  const [canValueBeShown, setCanValueBeShown] = useState(
    thisTrade
      ? isAbleToShowCashColumn({
          params,
          thisTrade,
          isOp,
          isLeg,
          isER,
          isOutright,
        })
      : false,
  );

  // AG Grid colDefs are deliberately not rebuilt on theme change (LQC-2259),
  // so params.theme can be stale - read the live theme instead.
  const { theme } = useTheme();
  const gridSelectColors = getGridSelectColors(theme);
  const rowHeight = useTableUIStore((state) => state.rowHeight);
  const fontSizeSetting = useTableUIStore((state) => state.fontSize);
  const cellElementHeight = getCellElementHeight(rowHeight);
  const cellFontSize = getCellFontSizeRem(fontSizeSetting);

  // TODO: Change this to use the existing Helpers:
  // TODO: updateFieldOnParamsToQuote
  // TODO: updateQuotesAcrossTables
  function handleChange(selection: any) {
    if (thisTrade) {
      if (!isOp && isER && !isOutright) {
        //RP ER
        if (!thisTrade.fees) {
          return;
        }
        const status = thisTrade.status as ExecutionRequestStatus;
        const feeKey = returnFeeObjectKeyRPatER(status);
        const updatedFees = thisTrade!.fees.map((feeObj) => {
          // nested in if(thisTrade)
          return feeObj.transactionFeeSide == thisTrade!.quoteSideER
            ? {
                ...feeObj,
                [feeKey]: selection.toString(),
                userUpdatedDirection: selection.toString(),
              } // Dynamically loads the feeKey.
            : { ...feeObj };
        });
        const newData = currentTradeData.map((trade) => {
          return trade.esid == fullTradeData.esid
            ? {
                ...trade,
                fees: updatedFees,
              }
            : trade;
        });
        tradeDataStore.getState().updateTradeData(newData);
      } else if ((!isOp && isOutright) || (!isOp && isLeg && thisTrade.legs)) {
        if (!thisTrade.legs || thisTrade.legs.length < 0) {
          return;
        }
        const status = thisTrade.status as ExecutionRequestStatus;

        const newThisTradeLegs = thisTrade.legs.map((leg) => {
          const feeKey = returnFeeDirectionObjectKeyRPLeg({
            status,
            unwind: thisTrade!.unwind,
            legUnwind: leg.unwind,
          });
          return leg.blockId == returnBlockId(thisTrade!, params)
            ? {
                ...leg,
                fees: leg.fees!.map((feeObj) =>
                  feeObj.transactionFeeSide == leg.quoteSide
                    ? {
                        ...feeObj, // Dynamically loads the feeKey.
                        [feeKey]: selection.toString(), // Mapping the nested data on the nested legs.
                        userUpdatedDirection: selection.toString(),
                      }
                    : { ...feeObj },
                ),
              }
            : leg;
        });
        const newThisTrade = { ...thisTrade, legs: newThisTradeLegs };
        const newData = currentTradeData.map((trade) => {
          return trade.esid == fullTradeData.esid
            ? {
                ...newThisTrade,
              }
            : trade;
        });
        tradeDataStore.getState().updateTradeData(newData);
      }
    }
  }

  useEffect(() => {
    if (thisTrade) {
      const dealer = determineDealerFromImpliedValues(
        params,
        thisTrade,
        isOutright,
      );
      setSelectedDealer(dealer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // showNeedToRecouponOrEnterCash - Function that checks fee transaction and if the value was empty before getting updated.
  // If the value in feeTransaciton was empty, that means the user has now entered the value BUT they are yet to send it.
  // const [warningNoRecouponOrCashEntered, setwarningNoRecouponOrCashEntered] =
  //   useState(
  //     thisTrade ? showNeedToRecouponOrEnterCash(params, thisTrade, isOutright) : false,
  //   );

  useEffect(() => {
    if (thisTrade) {
      const dealer = determineDealerFromImpliedValues(
        params,
        thisTrade,
        isOutright,
      );
      if (dealer) {
        setSelectedDealer(dealer);
      }
      setCanValueBeShown(
        isAbleToShowCashColumn({
          params,
          thisTrade,
          isOp,
          isLeg,
          isER,
          isOutright,
        }),
      );
      // setwarningNoRecouponOrCashEntered(
      //   showNeedToRecouponOrEnterCash(params, thisTrade, isOutright),
      // );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thisTrade]);

  function returnSelectMenu(): boolean {
    if (
      (isOp && canValueBeShown) ||
      (!isOp &&
        thisTrade?.status &&
        statusFormExecutedToAffirmedRange.includes(thisTrade.status) &&
        canValueBeShown) ||
      ExecutionRequestStatus.DealtAway == thisTrade?.status
    ) {
      return false; // <div>{selectedDealer}</div>;
    }
    return true;
  }

  if (shouldRenderDealerCell(params, unwind, isLeg, isOutright)) {
    return canValueBeShown ? (
      <div id="dealerContainer" className="flex h-full items-center">
        {returnSelectMenu() ? (
          <div>
            <Select
              labelId="demo-small-select-label cube-select-styles py-2"
              id={`dealerDropdown`}
              className="right-2 flex items-center p-0 text-xs"
              style={{ height: `${cellElementHeight}px` }}
              sx={{
                ...gridSelectColors.control,
                "& .MuiSelect-icon": gridSelectColors.icon,
              }}
              MenuProps={{
                sx: {
                  "& .MuiMenu-paper": {
                    paddingTop: "0px !important",
                    paddingBottom: "0px !important",
                    width: "120px",
                    ...gridSelectColors.menuPaper,
                  },
                  "& .MuiMenuItem-root": {
                    paddingLeft: "5px !important",
                    paddingRight: "5px !important",
                    minWidth: "120px !important",
                    height: `${cellElementHeight + 3}px`,
                    fontSize: cellFontSize,
                    backgroundColor: gridSelectColors.menuPaper.backgroundColor,
                    color: gridSelectColors.menuPaper.color,
                  },
                  "& .MuiMenuItem-root:hover": gridSelectColors.menuItemHover,
                },
              }}
              displayEmpty={true}
              value={selectedDealer}
              renderValue={() => (
                <div
                  className="relative right-1 top-[-1px] min-w-[75px]"
                  style={{ fontSize: cellFontSize }}
                >
                  Dealer {selectedDealer}
                </div>
              )}
              onChange={(e) => {
                if (e.target.value) {
                  handleChange(e.target.value);
                }
              }}
            >
              {/* IF THE RP cannot change the drop down at recieved - 14178 maybe the options are not there? */}

              <MenuItem key={"Pays"} value={"Pays"} title={"Pays"}>
                Dealer Pays
              </MenuItem>
              <MenuItem key={"Receives"} value={"Receives"} title={"Receives"}>
                Dealer Receives
              </MenuItem>
            </Select>
          </div>
        ) : (
          <div id="dealerDirection">Dealer {selectedDealer}</div>
        )}
      </div>
    ) : (
      <div id="noDealer"></div>
    );
  } else return <div id="noDealer"></div>;
};

export default DealerCellRenderer;
